import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BottomTabBar from '@/components/BottomTabBar';
import { useAuth } from '@/lib/AuthContext';
import { useChallengeState } from '@/components/useChallengeState';

// Tab pages - imported directly to keep them mounted
import MapPage from '@/pages/MapPage';
import LocalFeed from '@/pages/LocalFeed';
import Challenges from '@/pages/Challenges';
import Profile from '@/pages/Profile';

// Tab ID from pathname (prefix match)
function getTabFromPath(pathname) {
  if (pathname.startsWith('/feed')) return 'feed';
  if (pathname.startsWith('/challenges')) return 'challenges';
  if (pathname.startsWith('/profile')) return 'profile';
  return 'map'; // default for /map, /, /court, etc.
}

// Initial paths for each tab
const DEFAULT_PATHS = {
  map: '/map',
  feed: '/feed',
  challenges: '/challenges',
  profile: '/profile'
};

// Tab components mapping
const TAB_COMPONENTS = {
  map: MapPage,
  feed: LocalFeed,
  challenges: Challenges,
  profile: Profile
};

export default function TabsLayout() {
  const { user } = useAuth();
  const { unviewedCount } = useChallengeState(user);
  const location = useLocation();
  const navigate = useNavigate();

  // Track last visited path per tab (preserves sub-routes & scroll context)
  const [lastPathByTab, setLastPathByTab] = useState(DEFAULT_PATHS);

  // Track which tabs have been visited (lazy mount)
  const [visitedTabs, setVisitedTabs] = useState({ map: true });

  const currentTab = useMemo(() => getTabFromPath(location.pathname), [location.pathname]);

  // Redirect "/" to "/map" for URL consistency
  useEffect(() => {
    if (location.pathname === '/') {
      navigate('/map', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Update lastPathByTab and visitedTabs when location changes
  useEffect(() => {
    const fullPath = location.pathname + location.search + location.hash;

    setLastPathByTab(prev => {
      if (prev[currentTab] === fullPath) return prev;
      return { ...prev, [currentTab]: fullPath };
    });

    setVisitedTabs(prev => {
      if (prev[currentTab]) return prev;
      return { ...prev, [currentTab]: true };
    });
  }, [location.pathname, location.search, location.hash, currentTab]);

  // Navigate to last known path for target tab
  const handleTabClick = useCallback((tabId) => {
    const targetPath = lastPathByTab[tabId] || DEFAULT_PATHS[tabId];

    // If same tab, go to root (toggle behavior)
    if (tabId === currentTab && location.pathname !== DEFAULT_PATHS[tabId]) {
      navigate(DEFAULT_PATHS[tabId]);
    } else {
      navigate(targetPath);
    }
  }, [lastPathByTab, location.pathname, navigate, currentTab]);

  return (
    <div
      className="h-screen flex flex-col"
      style={{ paddingBottom: 'calc(3rem + env(safe-area-inset-bottom))' }}
    >
      <div className="flex-1 overflow-hidden relative">
        {/* Keep all visited tabs mounted, show/hide based on current tab */}
        {Object.entries(TAB_COMPONENTS).map(([tabId, Component]) => {
          const isActive = tabId === currentTab;
          const hasBeenVisited = visitedTabs[tabId];

          // Only render if visited (lazy mount) or active
          if (!hasBeenVisited && !isActive) return null;

          const overflowClass = tabId === 'map' ? 'overflow-hidden' : 'overflow-auto';

          return (
            <div
              key={tabId}
              className={`absolute inset-0 ${overflowClass}`}
              style={{
                visibility: isActive ? 'visible' : 'hidden',
                pointerEvents: isActive ? 'auto' : 'none',
                zIndex: isActive ? 1 : 0
              }}
            >
              <Component isActive={isActive} />
            </div>
          );
        })}
      </div>
      <BottomTabBar
        unviewedChallengesCount={unviewedCount}
        onTabClick={handleTabClick}
      />
    </div>
  );
}
